import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { isSupabaseConfigured, supabase } from '../lib/supabase'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(isSupabaseConfigured)

  const resolveAdmin = useCallback(async (nextSession) => {
    if (!supabase || !nextSession?.user) {
      return false
    }

    const { data, error } = await supabase.rpc('is_admin')
    if (error) {
      console.error('Admin authorization check failed:', error)
      return false
    }

    return Boolean(data)
  }, [])

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return undefined
    }

    let isMounted = true
    let authUpdateVersion = 0
    let deferredAuthUpdate = null

    const applySession = async (nextSession) => {
      const updateVersion = ++authUpdateVersion
      setSession(nextSession)

      const admin = await resolveAdmin(nextSession)
      if (!isMounted || updateVersion !== authUpdateVersion) return

      setIsAdmin(admin)
      setLoading(false)
    }

    supabase.auth.getSession()
      .then(({ data }) => {
        if (isMounted) void applySession(data.session)
      })
      .catch((error) => {
        console.error('Initial session check failed:', error)
        if (!isMounted) return
        setSession(null)
        setIsAdmin(false)
        setLoading(false)
      })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      // Supabase auth callbacks must return immediately. Starting another
      // Supabase request here can block public queries during initialisation.
      if (deferredAuthUpdate !== null) window.clearTimeout(deferredAuthUpdate)
      deferredAuthUpdate = window.setTimeout(() => {
        deferredAuthUpdate = null
        if (isMounted) void applySession(nextSession)
      }, 0)
    })

    return () => {
      isMounted = false
      authUpdateVersion += 1
      if (deferredAuthUpdate !== null) window.clearTimeout(deferredAuthUpdate)
      subscription.unsubscribe()
    }
  }, [resolveAdmin])

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      isAdmin,
      loading,
      configured: isSupabaseConfigured,
      signIn: async (email, password) => {
        if (!supabase) throw new Error('Supabase is not configured.')
        const result = await supabase.auth.signInWithPassword({ email, password })
        const admin = result.data.session ? await resolveAdmin(result.data.session) : false
        setIsAdmin(admin)
        return { ...result, isAdmin: admin }
      },
      signOut: async () => {
        if (!supabase) return
        await supabase.auth.signOut()
        setIsAdmin(false)
      },
    }),
    [isAdmin, loading, resolveAdmin, session],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
